import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useRemoveDocumentLimitDefinitionMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export const CashLimits = () => {
  const navigate = useNavigate();
  const [removeDefinition] = useRemoveDocumentLimitDefinitionMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      return;
    }

    match(actionKey)
      .with('DETAIL', 'EDIT', () =>
        navigate(composePath(settingsRoutes.cashLimitsEdit, {params: {id}}))
      )
      .with('DELETE', () =>
        openDeleteDialog({
          text: i18n.t('entity.cashRegisterLimits.notifications.deleteLimitDefinitionNotification'),
          onConfirm: () =>
            removeDefinition({id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.accounting.labels.cashRegisterLimitsTitle');

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accounting.actions.addDefinition'),
        onClick: () => {
          navigate(settingsRoutes.cashLimitsAdd);
        },
        'data-testid': testIds.settings.cashLimits('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate header={header} data-testid="settings-cash-limits">
        <DmsDataGrid
          gridCode="accounting-cash-limit"
          actionCallback={actionCallback}
          data-testid={testIds.settings.cashLimits('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
};
